import React from 'preact/compat';
import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import {
    ADD_FAILURE,
    ADD_LOADING,
    ADD_REQUEST,
    ADD_SUCCESS,
    ADD_ITEMS_REQUEST,
    ADD_ITEMS_LOADING,
    ADD_ITEMS_SUCCESS,
    ADD_ITEMS_FAILURE,
    ADD_ITEMS_ABORTED,
    REMOVE_REQUEST,
    REMOVE_LOADING,
    REMOVE_SUCCESS,
    REMOVE_FAILURE,
    FavouritesEvent,
    FavouritesPayload,
    SingleItemPayload,
    publish,
    InspirationEvent,
    InspirationPayload,
    ADD_INSPIRATION_FAILURE,
    ADD_INSPIRATION_LOADING,
    ADD_INSPIRATION_REQUEST,
    ADD_INSPIRATION_SUCCESS,
    REMOVE_INSPIRATION_LOADING,
    REMOVE_INSPIRATION_SUCCESS,
    REMOVE_INSPIRATION_FAILURE,
    REMOVE_INSPIRATION_REQUEST,
} from 'list/pubsub';

import { UPDATE_DATA, UpdateDataPayload, UpdateDataEvent } from 'helpers/pubsub';

import {
    addItems,
    addInspirations,
    getLists,
    setSelected,
    removeItem,
    removeInspirations,
    FAVOURITES_IGNORED_ERROR_CODES,
} from 'apis/list';
import { removeItemFromStore, updateInspirationsStore, refreshCache, updateMultiListStore } from 'list/helpers/store';
import {
    logAddItemsToFavourites,
    logRemoveItemFromFavourites,
    logAddInspiration,
    logRemoveInspiration,
    logShowInspirations,
} from 'helpers/analytics';
import { reportError } from 'helpers/sentry';
import { List, RetailItem } from 'apis/list/graphqlTypes';
import { favouritesUrl } from 'utils/settings';
import AddToList from 'list/components/AddToList';
import RemoveFromList from 'list/components/RemoveFromList';
import translate from 'list/utils/translate';
import { track } from 'helpers/optimizely';
import fetchPhraseIfNeeded from 'list/utils/fetch-phrase-if-needed';

const INITIAL_PAYLOAD = {
    productName: '',
    productNumber: '',
    productType: '',
    quantity: '',
    ratings: -1,
    source: '',
    alwaysShowModal: false,
};

export const AgentModal = () => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [addPayload, setAddPayload] = useState<FavouritesPayload>(INITIAL_PAYLOAD);
    const [removePayload, setRemovePayload] = useState<SingleItemPayload>(INITIAL_PAYLOAD);
    const [lists, setLists] = useState<List[]>([]);
    const defaultListName = () => translate('Default list name');

    useEffect(() => {
        window.ikea.pubsub.subscribe<FavouritesEvent>(ADD_REQUEST, handleAddToListSubscription);
        window.ikea.pubsub.subscribe<FavouritesEvent>(REMOVE_REQUEST, handleRemoveFromListSubscription);
        window.ikea.pubsub.subscribe<FavouritesEvent>(ADD_ITEMS_REQUEST, handleAddToListSubscription);
        return () => {
            window.ikea.pubsub.unsubscribe(ADD_REQUEST, handleAddToListSubscription);
            window.ikea.pubsub.unsubscribe(REMOVE_REQUEST, handleRemoveFromListSubscription);
            window.ikea.pubsub.unsubscribe(ADD_ITEMS_REQUEST, handleAddToListSubscription);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.ikea.pubsub.subscribe<InspirationEvent>(ADD_INSPIRATION_REQUEST, handleAddInspirationSubscription);
        return () => {
            window.ikea.pubsub.unsubscribe(ADD_INSPIRATION_REQUEST, handleAddInspirationSubscription);
        };
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        window.ikea.pubsub.subscribe<InspirationEvent>(REMOVE_INSPIRATION_REQUEST, handleRemoveInspirationSubscription);
        return () => {
            window.ikea.pubsub.unsubscribe(REMOVE_INSPIRATION_REQUEST, handleRemoveInspirationSubscription);
        };
    }, []);

    useEffect(() => {
        window.ikea.pubsub.subscribe<UpdateDataEvent>(UPDATE_DATA, handleUpdateDataSubscription);
        return () => {
            window.ikea.pubsub.unsubscribe(UPDATE_DATA, handleUpdateDataSubscription);
        };
    }, []);

    const handleCloseRemoveModal = () => {
        publish(ADD_ITEMS_ABORTED, removePayload);
        setIsRemoveModalOpen(false);
        setRemovePayload(INITIAL_PAYLOAD);
    };

    const handleCloseAddModal = () => {
        publish(ADD_ITEMS_ABORTED, addPayload);
        setIsAddModalOpen(false);
        setAddPayload(INITIAL_PAYLOAD);
    };

    const handleModalClose = isAddModalOpen ? handleCloseAddModal : handleCloseRemoveModal;

    const handleAddToListSubscription = (payload: FavouritesPayload) => {
        return Promise.all([getLists(), fetchPhraseIfNeeded()])
            .then(([res]) => {
                if (res.data.lists.length <= 1 && !payload.alwaysShowModal) {
                    return addToList(payload);
                }
                // @ts-ignore
                setLists(res.data.lists);
                setAddPayload(payload);
                setIsAddModalOpen(true);
                setIsRemoveModalOpen(false);
                setRemovePayload(INITIAL_PAYLOAD);
            })
            .catch(e => {
                reportError(e as Error);
            });
    };

    const handleRemoveFromListSubscription = (payload: FavouritesPayload) =>
        Promise.all([getLists(), fetchPhraseIfNeeded()])
            .then(([res]) => {
                const { matchingLists, listItemQuantityMap } = listsContainingItem(
                    res.data.lists as List[],
                    (payload as SingleItemPayload).productNumber
                );

                if (res.data.lists.length <= 1 || matchingLists.length === 0) {
                    return removeFromLists(payload as SingleItemPayload, undefined, undefined, listItemQuantityMap);
                }
                // @ts-ignore
                setLists(res.data.lists);
                setRemovePayload(payload as SingleItemPayload);
                setIsRemoveModalOpen(true);
                setAddPayload(INITIAL_PAYLOAD);
                setIsAddModalOpen(false);
            })
            .catch(e => {
                reportError(e as Error);
            });

    const moveToInspirations = () => {
        logShowInspirations();
        window.location.href = `${favouritesUrl}inspiration`;
    };

    const handleAddInspirationSubscription = async (payload: InspirationPayload) => {
        try {
            await fetchPhraseIfNeeded();
            publish(ADD_INSPIRATION_LOADING, payload);
            await addInspirations(payload.ids)
                .then(response => {
                    if (!response?.errors) {
                        logAddInspiration(payload);
                        updateInspirationsStore(response?.data?.inspirations);
                        publish(ADD_INSPIRATION_SUCCESS, payload);
                        window?.ikea?.toast(
                            translate('add_inspiration_success'),
                            {
                                label: translate('Show'),
                                callbackOrHref: moveToInspirations,
                            },
                            null
                        );
                    }
                })
                .catch(() => {
                    publish(ADD_INSPIRATION_FAILURE, payload);
                    window?.ikea?.toast(translate('GENERIC_ERROR'));
                });
        } catch (e) {
            reportError(e as Error);
        }
    };

    const handleRemoveInspirationSubscription = async (payload: InspirationPayload) => {
        try {
            await fetchPhraseIfNeeded();
            publish(REMOVE_INSPIRATION_LOADING, payload);
            await removeInspirations(payload.ids)
                .then(response => {
                    if (!response?.errors) {
                        logRemoveInspiration(payload);
                        updateInspirationsStore(response?.data?.inspirations);
                        publish(REMOVE_INSPIRATION_SUCCESS, payload);
                        window?.ikea?.toast(translate('Remove inspiration from favourites toast message'));
                    }
                })
                .catch(() => {
                    publish(REMOVE_INSPIRATION_FAILURE, payload);
                    window?.ikea?.toast(translate('GENERIC_ERROR'));
                });
        } catch (e) {
            reportError(e as Error);
        }
    };

    const handleUpdateDataSubscription = (payload: UpdateDataPayload) => {
        if (payload.source === 'favourites') {
            updateMultiListStore(payload.data);
        }
        if (payload.source === 'inspiration') {
            updateInspirationsStore(payload.data);
        }
    };

    const addToList = (
        payload: FavouritesPayload,
        listId?: string,
        name?: string,
        skipToast = true
    ): Promise<boolean> => {
        let formattedItemsPayload: Array<{
            itemNo: string;
            quantity: number;
            type: string;
        }>;
        if ('items' in payload) {
            formattedItemsPayload = payload.items.map(({ productNumber, productType, quantity }) => ({
                itemNo: productNumber,
                quantity: Number(quantity),
                type: productType,
            }));
        } else {
            formattedItemsPayload = [
                {
                    itemNo: payload.productNumber,
                    quantity: Number(payload.quantity),
                    type: payload.productType,
                },
            ];
        }
        const formattedGraphqlPayload = formattedItemsPayload.map(({ itemNo, quantity }) => ({
            itemNo,
            quantity,
        }));
        if ('items' in payload) {
            publish(ADD_ITEMS_LOADING, payload);
        } else {
            publish(ADD_LOADING, payload);
        }
        return addItems(formattedGraphqlPayload, listId)
            .then(async res => {
                try {
                    if (listId) {
                        await setSelected(listId);
                    }
                    if ('items' in payload) {
                        publish(ADD_ITEMS_SUCCESS, { ...payload, skipToast });
                    } else {
                        publish(ADD_SUCCESS, { ...payload, skipToast });
                    }
                    const toastText =
                        'items' in payload
                            ? translate('Items were added to the list', {
                                  count: String(formattedGraphqlPayload.length),
                                  listName: name && name !== '' ? name : defaultListName(),
                              })
                            : translate('Item was added to the list', {
                                  productName: payload.productName,
                                  listName: name && name !== '' ? name : defaultListName(),
                              });
                    const currentListId = listId ? listId : res.data.list.listId;
                    window?.ikea?.toast(
                        toastText,
                        {
                            label: translate('Show'),
                            callbackOrHref: `${favouritesUrl}${currentListId}/retail`,
                        },
                        null
                    );
                    refreshCache();
                    track('favourites_item_added_to_list');
                    logAddItemsToFavourites(payload.source, currentListId, formattedItemsPayload, payload?.ga);
                    setAddPayload(INITIAL_PAYLOAD);
                    setIsAddModalOpen(false);
                    return true;
                } catch (e) {
                    reportError(e as Error);
                    return false;
                }
            })
            .catch(e => {
                reportError(e as Error);
                if ('items' in payload) {
                    publish(ADD_ITEMS_FAILURE, { ...payload, skipToast });
                } else {
                    publish(ADD_FAILURE, { ...payload, skipToast });
                }

                window?.ikea?.toast(
                    e === 'ILLEGAL_QUANTITY' ? translate('ILLEGAL_QUANTITY_ERROR') : translate('GENERIC_ERROR'),
                    { label: translate('Show'), callbackOrHref: favouritesUrl },
                    null
                );
                return false;
            });
    };

    const removeFromLists = async (
        payload: SingleItemPayload,
        listNames?: string[],
        listIds?: string[],
        listItemQuantityMap?: Record<string, number>,
        keepIcon = false,
        skipToast = true
    ) => {
        publish(REMOVE_LOADING, payload);
        if (listIds) {
            try {
                for (let i = 0; i < listIds.length; i++) {
                    const quantity = (listItemQuantityMap && listItemQuantityMap[listIds[i]]) || 0;
                    await removeItem(payload.productNumber, listIds[i]);
                    removeItemFromStore(payload.productNumber);
                    logRemoveItemFromFavourites(
                        payload.source,
                        listIds[i],
                        payload.productNumber,
                        payload.productType,
                        quantity,
                        payload?.ga
                    );
                }
                const separatorLabel = translate('And');
                publish(REMOVE_SUCCESS, { ...payload, keepIcon, skipToast });
                window.ikea.toast(
                    translate('Item was removed from lists', {
                        productName: payload.productName,
                        listNames: listNames?.join().replace(/,([^,]*)$/, ` ${separatorLabel} $1`) ?? defaultListName(),
                    }),
                    { label: translate('Show'), callbackOrHref: favouritesUrl },
                    null
                );
                return true;
            } catch (e) {
                reportError(e as Error);
                publish(REMOVE_FAILURE, { ...payload, skipToast });
                window?.ikea?.toast(
                    translate('GENERIC_ERROR'),
                    { label: translate('Show'), callbackOrHref: favouritesUrl },
                    null
                );
                return false;
            }
        }
        try {
            const res = await removeItem(payload.productNumber);
            const quantity = (listItemQuantityMap && listItemQuantityMap[res.data.list.listId]) || 0;
            removeItemFromStore(payload.productNumber);
            publish(REMOVE_SUCCESS, { ...payload, skipToast });
            window?.ikea?.toast(
                translate('Item was removed from lists', {
                    productName: payload.productName,
                    listNames: defaultListName(),
                }),
                { label: translate('Show'), callbackOrHref: favouritesUrl },
                null
            );
            logRemoveItemFromFavourites(
                payload.source,
                res.data.list.listId,
                payload.productNumber,
                payload.productType,
                quantity,
                payload?.ga
            );
        } catch (e) {
            const ignoreError = !!FAVOURITES_IGNORED_ERROR_CODES.find(errorCode =>
                (e as Error).message?.includes(errorCode)
            );
            // If the error is one of the whitelisted ones, do not report it and simulate that the item was removed from the list successfully.
            // We can have this behaviour because the whitelisted errors occur due to sync issues between different browser tabs.
            if (ignoreError) {
                publish(REMOVE_SUCCESS, { ...payload, skipToast });
                window?.ikea?.toast(
                    translate('Item was removed from generic list', {
                        productName: payload.productName,
                    }),
                    { label: translate('Show'), callbackOrHref: favouritesUrl },
                    null
                );
                return true;
            }
            reportError(e as Error);
            publish(REMOVE_FAILURE, { ...payload, skipToast });
            window?.ikea?.toast(
                translate('GENERIC_ERROR'),
                { label: translate('Show'), callbackOrHref: favouritesUrl },
                null
            );
            return false;
        }
    };

    const moveToAnotherList = () => {
        setIsRemoveModalOpen(false);
        setIsAddModalOpen(true);
        setAddPayload({
            ...removePayload,
            quantity: '1',
        });
    };

    const listsContainingItem = (lists: List[], productNumber: string) => {
        const listItemQuantityMap: Record<string, number> = {};

        const matchingLists = lists.filter((list: List) =>
            list?.retail?.items?.find((item: RetailItem) => {
                listItemQuantityMap[list.listId] = item.quantity;
                return item.itemNo === productNumber;
            })
        );

        return { matchingLists, listItemQuantityMap };
    };

    return (
        <div>
            {isAddModalOpen && (
                <AddToList
                    modalOpen={isAddModalOpen}
                    handleClose={handleModalClose}
                    payload={addPayload}
                    addToList={addToList}
                    lists={lists}
                />
            )}
            {isRemoveModalOpen && (
                <RemoveFromList
                    modalOpen={isRemoveModalOpen}
                    closeModal={handleModalClose}
                    payload={removePayload}
                    removeFromLists={removeFromLists}
                    lists={listsContainingItem(lists, removePayload.productNumber).matchingLists}
                    moveToAnotherList={moveToAnotherList}
                />
            )}
        </div>
    );
};

export default AgentModal;
